import CryptoJS from 'crypto-js';

import { colors } from './defaultTheme';

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const parseJwt = (token) => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
};

const base64url = (source) => {
  // Encode in classical base64
  let encodedSource = CryptoJS.enc.Base64.stringify(source);

  // Remove padding equal characters
  encodedSource = encodedSource.replace(/=+$/, '');

  // Replace characters according to base64url specifications
  encodedSource = encodedSource.replace(/\+/g, '-');
  encodedSource = encodedSource.replace(/\//g, '_');

  return encodedSource;
};

export const createToken = (data, secret) => {
  const header = {
    alg: 'HS256',
    typ: 'JWT',
  };

  const stringifiedHeader = CryptoJS.enc.Utf8.parse(JSON.stringify(header));
  const encodedHeader = base64url(stringifiedHeader);

  const stringifiedData = CryptoJS.enc.Utf8.parse(JSON.stringify(data));
  const encodedData = base64url(stringifiedData);

  const token = `${encodedHeader}.${encodedData}`;

  let signature = CryptoJS.HmacSHA256(token, secret);
  signature = base64url(signature);

  return `${token}.${signature}`;
};

export const cleanSpaceString = (event) => {
  const newEvent = {
    ...event,
    target: { ...event.target, value: event.target?.value.trimStart() },
  };
  return newEvent;
};

export const getHeaderFooterColorData = (option, mainColor, logo, whiteLogo) => {
  const dataMap = new Map([
    [
      1,
      {
        backgroundColor: colors.white,
        iconColor: mainColor,
        textColor: mainColor,
        logo,
      },
    ],
    [
      2,
      {
        backgroundColor: colors.white,
        iconColor: colors.black,
        textColor: colors.black,
        logo,
      },
    ],
    [
      3,
      {
        backgroundColor: mainColor,
        iconColor: colors.white,
        textColor: colors.white,
        logo: whiteLogo,
      },
    ],
    [
      4,
      {
        backgroundColor: mainColor,
        iconColor: colors.black,
        textColor: colors.black,
        logo,
      },
    ],
  ]);

  return dataMap.get(option);
};

export const emptyString = (string) =>
  string === undefined || string === null || string === '' || string === ' ';

export const FileType = {
  pdf: '.pdf',
  eml: '.eml',
  msg: '.msg',
  docx: '.docx',
  rtf: '.rtf',
  doc: '.doc',
  dotm: '.dotm',
  docm: '.docm',
  excel: '.xlsx',
  htm: '.htm',
  html: '.html',
  css: '.css',
  xps: '.xps',
  jpg: '.jpg',
  png: '.png',
  svg: '.svg',
  ico: '.ico',
  gif: '.gif',
  jpeg: '.jpeg',
  bmp: '.bmp',
  tif: '.tif',
  tiff: '.tiff',
  tga: '.tga',
  pcx: '.pcx',
  psd: '.psd',
  pic: '.pic',
  emf: '.emf',
  jgw: '.jgw',
  dib: '.dib',
  heic: '.heic',
  image: '.image',
  music: '.music',
  mp3: '.mp3',
  wav: '.wav',
  cda: '.cda',
  m4a: '.m4a',
  vlc: '.vlc',
  video: '.video',
  mp4: '.mp4',
  wmv: '.wmv',
  wmp: '.wmp',
  mov: '.mov',
  mpeg: '.mpeg',
  mpg: '.mpg',
  vob: '.vob',
  avi: '.avi',
  divx: '.divx',
  mkv: '.mkv',
  db: '.db',
  mdt: '.mdt',
  mdb: '.mdb',
  ppt: '.ppt',
  pptx: '.pptx',
  pptm: '.pptm',
  pot: '.pot',
  potx: '.potx',
  potm: '.potm',
  ppa: '.ppa',
  ppam: '.ppam',
  ppsm: '.ppsm',
  pps: '.pps',
  ppsx: '.ppsx',
  sldx: '.sldx',
  sldm: '.sldm',
  zip: '.zip',
  rar: '.rar',
  sevenZ: '.7z',
  arj: '.arj',
  xlsx: '.xlsx',
  xlv: '.xlv',
  xlam: '.xlam',
  xlsb: '.xlsb',
  xltm: '.xltm',
  xltx: '.xltx',
  xlt: '.xlt',
  xlsm: '.xlsm',
  csv: '.csv',
  xls: '.xls',
};

export const getIconPdf = (iconType) =>
  iconType === FileType.pdf || iconType === FileType.eml || iconType === FileType.msg;

export const getIconDoc = (iconType) =>
  iconType === FileType.docx ||
  iconType === FileType.rtf ||
  iconType === FileType.doc ||
  iconType === FileType.dotm ||
  iconType === FileType.docm;

export const getIconExcel = (iconType) =>
  iconType === FileType.xlsx ||
  iconType === FileType.xlv ||
  iconType === FileType.xlam ||
  iconType === FileType.xlsb ||
  iconType === FileType.xltm ||
  iconType === FileType.xltx ||
  iconType === FileType.xlt ||
  iconType === FileType.xlsm ||
  iconType === FileType.csv ||
  iconType === FileType.xls;

export const getIconCode = (iconType) =>
  iconType === FileType.htm ||
  iconType === FileType.html ||
  iconType === FileType.css ||
  iconType === FileType.xps;

export const getIconSound = (iconType) =>
  iconType === FileType.music ||
  iconType === FileType.mp3 ||
  iconType === FileType.wav ||
  iconType === FileType.cda ||
  iconType === FileType.m4a;

export const getIconVideo = (iconType) =>
  iconType === FileType.vlc ||
  iconType === FileType.video ||
  iconType === FileType.mp4 ||
  iconType === FileType.wmv ||
  iconType === FileType.wmp ||
  iconType === FileType.mov ||
  iconType === FileType.mpeg ||
  iconType === FileType.mpg ||
  iconType === FileType.vob ||
  iconType === FileType.avi ||
  iconType === FileType.divx ||
  iconType === FileType.mkv;

export const getIconAccessDocument = (iconType) =>
  iconType === FileType.db || iconType === FileType.mdt || iconType === FileType.mdb;

export const getIconPpt = (iconType) =>
  iconType === FileType.ppt ||
  iconType === FileType.pptx ||
  iconType === FileType.pptm ||
  iconType === FileType.pot ||
  iconType === FileType.potx ||
  iconType === FileType.potm ||
  iconType === FileType.ppa ||
  iconType === FileType.ppam ||
  iconType === FileType.ppsm ||
  iconType === FileType.pps ||
  iconType === FileType.ppsx ||
  iconType === FileType.sldx ||
  iconType === FileType.sldm;

export const getIconZip = (iconType) =>
  iconType === FileType.zip ||
  iconType === FileType.rar ||
  iconType === FileType.sevenZ ||
  iconType === FileType.arj;

export const getIconImage = (iconType) =>
  iconType === FileType.jpg ||
  iconType === FileType.png ||
  iconType === FileType.svg ||
  iconType === FileType.gif ||
  iconType === FileType.jpeg ||
  iconType === FileType.bmp ||
  iconType === FileType.tif ||
  iconType === FileType.tiff ||
  iconType === FileType.tga ||
  iconType === FileType.pcx ||
  iconType === FileType.psd ||
  iconType === FileType.pic ||
  iconType === FileType.ico ||
  iconType === FileType.emf ||
  iconType === FileType.jgw ||
  iconType === FileType.dib ||
  iconType === FileType.heic ||
  iconType === FileType.image;

export const ALERT_TYPE_SUCCESS = 'success';
export const ALERT_TYPE_ERROR = 'error';
export const ALERT_TYPE_WARNING = 'warning';

export const typeOrganizations = [
  { id: '1', name: 'Persona física' },
  {
    id: '2',
    name: 'Persona jurídica',
  },
];

export const allowedExtensions = `
.txt,
.dwg,
.edg,
.vcf,
.odt,
.oft,
.ods,
.url,
.do,
.dxf,
.thmx,
.odp,
.log,
.unknown,
.wri,
.xlv,
.xlam,
.xlsb,
.xltm,
.xltx,
.xlt,
.xlsm,
.csv,
.xls,
.xlsx,
.rtf,
.doc,
.dotm,
.docm,
.docx,
.rar,
.7z,
.arj,
.zip,
.gif,
.jpeg,
.jpg,
.png,
.bmp,
.tif,
.tiff,
.tga,
.pcx,
.psd,
.pic,
.emf,
.ico,
.jgw,
.dib,
.svg,
.heic,
.image,
.pdf,
.ppt,
.pptx,
.pptm,
.pot,
.potx,
.potm,
.ppa,
.ppam,
.ppsm,
.pps,
.ppsx,
.sldx,
.sldm,
.db,
.mdt,
.mdb,
.vlc,
.video,
.mp4,
.wmv,
.wmp,
.mov,
.mpeg,
.mpg,
.vob,
.avi,
.divx,
.mkv,
.music,
.mp3,
.wav,
.cda,
.m4a,
.htm,
.xml,
.html,
.css,
.xml,
.xps,
.eml,
.msg,
.,`;

export const validExtensionTypes = [
  '.txt',
  '.dwg',
  '.edg',
  '.vcf',
  '.odt',
  '.oft',
  '.ods',
  '.url',
  '.do',
  '.dxf',
  '.thmx',
  '.odp',
  '.log',
  '.unknown',
  '.wri',
  '.xlv',
  '.xlam',
  '.xlsb',
  '.xltm',
  '.xltx',
  '.xlt',
  '.xlsm',
  '.csv',
  '.xls',
  '.xlsx',
  '.rtf',
  '.doc',
  '.dotm',
  '.docm',
  '.docx',
  '.rar',
  '.7z',
  '.arj',
  '.zip',
  '.gif',
  '.jpeg',
  '.jpg',
  '.png',
  '.bmp',
  '.tif',
  '.tiff',
  '.tga',
  '.pcx',
  '.psd',
  '.pic',
  '.emf',
  '.ico',
  '.jgw',
  '.dib',
  '.svg',
  '.heic',
  '.image',
  '.pdf',
  '.ppt',
  '.pptx',
  '.pptm',
  '.pot',
  '.potx',
  '.potm',
  '.ppa',
  '.ppam',
  '.ppsm',
  '.pps',
  '.ppsx',
  '.sldx',
  '.sldm',
  '.db',
  '.mdt',
  '.mdb',
  '.vlc',
  '.video',
  '.mp4',
  '.wmv',
  '.wmp',
  '.mov',
  '.mpeg',
  '.mpg',
  '.vob',
  '.avi',
  '.divx',
  '.mkv',
  '.music',
  '.mp3',
  '.wav',
  '.cda',
  '.m4a',
  '.htm',
  '.xml',
  '.html',
  '.css',
  '.xml',
  '.xps',
  '.eml',
  '.msg',
];

export const getBestFontContrast = (color) => {
  let hexColor = color;
  if (hexColor.slice(0, 1) === '#') {
    hexColor = hexColor.slice(1);
  }
  if (hexColor.length === 3) {
    hexColor = hexColor
      .split('')
      .map((hex) => {
        return hex + hex;
      })
      .join('');
  }
  const r = parseInt(hexColor.slice(0, 2), 16);
  const g = parseInt(hexColor.slice(2, 4), 16);
  const b = parseInt(hexColor.slice(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155 ? '#000' : '#fff';
};

export const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const normalizeString = (str) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};
